import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { statusColor } from '../../shared/statusColors';
import { hasValidCoords } from '../../lib/geo';

// Default centre — Leamington Spa (matches the geocode bias).
const DEFAULT_CENTER = [52.287, -1.536];

// Tile provider (PLAN_admin_portal_phase1.md §2 A5): OpenStreetMap's own
// tile servers return 403 for sustained app traffic (tile usage policy).
// CARTO basemaps (light_all) is OSM-derived, key-free, and attribution-
// compliant — attribution string below satisfies the ODbL/CARTO terms.
const TILE_URL = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

// A job's map point: its own site coordinates win; otherwise fall back to
// the linked property's coordinates (migration 021 links jobs to
// properties). Null when neither exists.
function jobPoint(job) {
  if (hasValidCoords(job.site_lat, job.site_lng)) return [Number(job.site_lat), Number(job.site_lng)];
  if (hasValidCoords(job.property_lat, job.property_lng)) return [Number(job.property_lat), Number(job.property_lng)];
  return null;
}
const clientPoint = (client) => (hasValidCoords(client.lat, client.lng) ? [Number(client.lat), Number(client.lng)] : null);

function makeJobIcon(status) {
  const colors = statusColor(status);
  return L.divIcon({
    className: 'job-marker',
    html: `<span class="job-marker-dot" style="background:${colors.border};box-shadow:0 0 0 3px ${colors.bg}"></span>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -8],
  });
}

function makeClientIcon() {
  return L.divIcon({
    className: 'client-marker',
    html: '<span class="client-marker-dot"></span>',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -10],
  });
}

// Fit the view to every marker on show; falls back to the default centre
// when nothing has coordinates yet.
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) {
      map.setView(DEFAULT_CENTER, 12);
      return;
    }
    map.fitBounds(points, { padding: [48, 48], maxZoom: 14 });
  }, [map, points]);
  return null;
}

// Advanced jobs map — two layers (job markers colour-coded by status,
// client home markers in the brand colour), an all-vs-date-window scope
// toggle, a legend, and auto-fitting bounds. Jobs without coordinates are
// counted and explained, never silently dropped (docs/pass4_dev_brief.md §4).
export default function JobsMap({ jobs, allJobs = [], clients = [], clientName, onEditJob }) {
  const [layer, setLayer] = useState('both'); // 'jobs' | 'clients' | 'both'
  const [scope, setScope] = useState('window'); // 'window' (date-filtered) | 'all'

  const showJobs = layer !== 'clients';
  const showClients = layer !== 'jobs';

  const shownJobs = useMemo(
    () => (scope === 'all' ? allJobs : jobs).filter((job) => jobPoint(job)),
    [scope, jobs, allJobs],
  );
  const shownClients = useMemo(
    () => clients.filter((client) => clientPoint(client)),
    [clients],
  );

  const missingJobs = useMemo(
    () => (scope === 'all' ? allJobs : jobs).length - shownJobs.length,
    [scope, jobs, allJobs, shownJobs],
  );
  const missingClients = clients.length - shownClients.length;

  const points = useMemo(() => {
    const pts = [];
    if (showJobs) for (const job of shownJobs) pts.push(jobPoint(job));
    if (showClients) for (const client of shownClients) pts.push(clientPoint(client));
    return pts;
  }, [showJobs, showClients, shownJobs, shownClients]);

  const hasAllScope = allJobs.length > jobs.length;

  return (
    <div className="jobs-map">
      <div className="jobs-map-header">
        <div className="map-toggle-group" role="group" aria-label="Map layers">
          {[
            ['both', 'Jobs + clients'],
            ['jobs', 'Jobs only'],
            ['clients', 'Clients only'],
          ].map(([value, label]) => (
            <button
              key={value}
              className={`chip ${layer === value ? 'chip-active' : ''}`}
              onClick={() => setLayer(value)}
            >
              {label}
            </button>
          ))}
        </div>
        {hasAllScope && (
          <div className="map-toggle-group" role="group" aria-label="Map scope">
            <button className={`chip ${scope === 'window' ? 'chip-active' : ''}`} onClick={() => setScope('window')}>
              Date window
            </button>
            <button className={`chip ${scope === 'all' ? 'chip-active' : ''}`} onClick={() => setScope('all')}>
              All jobs
            </button>
          </div>
        )}
        <div className="jobs-map-legend">
          {showJobs && (
            <span className="legend-item"><span className="legend-dot legend-dot-scheduled" />Scheduled</span>
          )}
          {showJobs && (
            <span className="legend-item"><span className="legend-dot legend-dot-progress" />In progress</span>
          )}
          {showJobs && (
            <span className="legend-item"><span className="legend-dot legend-dot-complete" />Completed</span>
          )}
          {showClients && (
            <span className="legend-item"><span className="legend-dot legend-dot-client" />Client</span>
          )}
        </div>
      </div>

      <MapContainer center={DEFAULT_CENTER} zoom={12} className="jobs-map-canvas" scrollWheelZoom={false}>
        <TileLayer attribution={TILE_ATTRIBUTION} url={TILE_URL} />
        <FitBounds points={points} />
        {showJobs && shownJobs.map((job) => (
          <Marker
            key={job.id}
            position={jobPoint(job)}
            icon={makeJobIcon(job.status)}
          >
            <Popup>
              <strong>{job.title}</strong>
              <br />
              {clientName(job.client_id)}
              <br />
              <small>
                {job.date || ''}{job.engineer ? ` · ${job.engineer}` : ''}
              </small>
              <br />
              {onEditJob && (
                <button className="link-button" onClick={() => onEditJob(job)}>Edit job</button>
              )}
            </Popup>
          </Marker>
        ))}
        {showClients && shownClients.map((client) => (
          <Marker
            key={client.id}
            position={[Number(client.lat), Number(client.lng)]}
            icon={makeClientIcon()}
          >
            <Popup>
              <strong>{client.name}</strong>
              <br />
              <small>{client.address || 'No address'}</small>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {(missingJobs > 0 || missingClients > 0) && (
        <p className="field-hint map-missing-note">
          {missingJobs > 0 ? `${missingJobs} job${missingJobs > 1 ? 's' : ''} not shown — missing site coordinates. ` : ''}
          {missingClients > 0 ? `${missingClients} client${missingClients > 1 ? 's' : ''} without coordinates — add an address to place them.` : ''}
        </p>
      )}
    </div>
  );
}
